import { useEffect, useState } from "react";
import { Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { api, fmtErr } from "../../lib/api";

export default function BillingTab({ restaurant, onChanged }) {
  const [plans, setPlans] = useState(null);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payingBill, setPayingBill] = useState(false);
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    api.get(`/restaurants/${restaurant.id}/sms-usage`).then((r) => setUsage(r.data)).catch(() => {});
  }, [restaurant.id]);

  const payBill = async () => {
    setPayingBill(true);
    try {
      const { data } = await api.post("/payments/pay-bill", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(fmtErr(e));
      setPayingBill(false);
    }
  };
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/payments/mine").then((r) => setPayments(r.data)).catch(() => {});
    api.get(`/restaurants/${restaurant.id}/invoices`).then((r) => setInvoices(r.data)).catch(() => {});
  }, []);

  const subscribe = async (lookupKey) => {
    setLoadingKey(lookupKey);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(fmtErr(e));
      setLoadingKey(null);
    }
  };

  if (!plans) return <div />;

  return (
    <div data-testid="billing-tab" className="space-y-10">
      {restaurant.subscription_status === "past_due" && (
        <div data-testid="past-due-banner" className="rounded-2xl border border-amber-400/40 bg-amber-400/10 p-5">
          <p className="font-outfit font-medium text-amber-600 dark:text-amber-300">Subscription payment failed</p>
          <p className="text-sm text-muted-foreground mt-0.5">
            We couldn't collect your latest payment — Stripe retries automatically for a few days. If it keeps failing, your plan moves to Free. Re-subscribe below anytime to update your card.
          </p>
        </div>
      )}
      {restaurant.subscription_status === "canceled" && restaurant.subscription_tier === "free" && (
        <div data-testid="sub-canceled-banner" className="rounded-2xl border border-border/60 bg-card p-5">
          <p className="font-outfit font-medium">Your paid subscription has ended</p>
          <p className="text-sm text-muted-foreground mt-0.5">You're on the Free plan. Re-subscribe below to restore your features.</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Object.entries(plans).map(([key, p]) => {
          const current = restaurant.subscription_tier === key;
          return (
            <div key={key} data-testid={`plan-card-${key}`}
              className={`rounded-2xl border p-7 transition-transform duration-300 hover:-translate-y-[2px] ${
                key === "starter" ? "border-primary/60 bg-primary/5" : "border-border/60 bg-card"
              }`}>
              <div className="flex items-center justify-between">
                <h3 className="font-outfit font-medium text-xl">{p.name}</h3>
                {current && <Badge data-testid={`current-plan-${key}`} className="bg-primary text-primary-foreground">Current</Badge>}
                {key === "pro" && !current && <Sparkles className="w-4 h-4 text-primary" />}
              </div>
              <p className="mt-4">
                <span className="font-outfit font-semibold text-4xl">£{p.price}</span>
                <span className="text-muted-foreground text-sm">/month</span>
              </p>
              <ul className="mt-6 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className="text-sm text-muted-foreground flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              {!current && p.lookup_key && (
                <Button data-testid={`subscribe-${key}-btn`} onClick={() => subscribe(p.lookup_key)}
                  disabled={loadingKey !== null} className="w-full mt-8 rounded-full">
                  {loadingKey === p.lookup_key ? "Redirecting..." : `Upgrade to ${p.name}`}
                </Button>
              )}
              {current && key === "free" && (
                <p className="text-xs text-muted-foreground mt-8 text-center">You're on the free tier</p>
              )}
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <h3 className="font-outfit font-medium text-lg">Per-booking commission</h3>
          {restaurant.billing_enabled === false ? (
            <p className="text-xs text-emerald-500 mt-1" data-testid="fees-waived-note">
              Platform fees are currently waived for your account — nothing accrues on bookings.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground mt-1">
              A fee of £{plans[restaurant.subscription_tier]?.commission.toFixed(2)} is accrued per confirmed booking and invoiced monthly.
            </p>
          )}
          <p className="mt-5">
            <span data-testid="commission-balance" className="font-outfit font-semibold text-4xl">
              £{(restaurant.commission_balance || 0).toFixed(2)}
            </span>
            <span className="text-muted-foreground text-sm ml-2">outstanding</span>
          </p>
          {(restaurant.commission_balance || 0) > 0 && (
            <Button data-testid="pay-bill-btn" onClick={payBill} disabled={payingBill} className="mt-5 rounded-full">
              {payingBill ? "Redirecting..." : "Pay outstanding balance"}
            </Button>
          )}
        </div>
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <h3 className="font-outfit font-medium text-lg">Payment history</h3>
          <div className="mt-4 space-y-3">
            {payments.map((p) => (
              <div key={p.session_id} data-testid={`payment-row-${p.session_id}`}
                className="flex items-center justify-between text-sm rounded-xl border border-border/40 px-4 py-3">
                <span className="capitalize">{p.lookup_key.replace("_", " ")}</span>
                <span className="text-muted-foreground">£{(p.amount / 100).toFixed(2)}</span>
                <Badge variant="outline" className={p.payment_status === "paid" ? "text-emerald-300 border-emerald-400/30" : "text-muted-foreground"}>
                  {p.payment_status}
                </Badge>
              </div>
            ))}
            {payments.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-payments-msg">No payments yet.</p>}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border/60 bg-card p-6">
        <div className="rounded-2xl border border-border/60 bg-card p-6" data-testid="sms-usage-card">
          <h3 className="font-outfit font-medium text-lg">SMS this month</h3>
          {usage ? (
            <div className="mt-3 space-y-1.5 text-sm">
              <p><span className="text-muted-foreground">Texts sent via platform Twilio:</span> <span data-testid="sms-texts-count">{usage.texts_sent}</span></p>
              <p><span className="text-muted-foreground">Booking surcharges (10p each):</span> <span data-testid="sms-fees-total">£{usage.sms_fees.toFixed(2)}</span></p>
              <p className="text-xs text-muted-foreground mt-2">Add your own Twilio keys in Settings → Messaging to stop the per-booking surcharge.</p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground mt-3">Loading…</p>
          )}
        </div>
        <h3 className="font-outfit font-medium text-lg">Commission invoices</h3>
        <p className="text-xs text-muted-foreground mt-1">Generated automatically on the 1st of each month for the previous month's bookings.</p>
        <div className="mt-4 space-y-3">
          {invoices.map((inv) => (
            <div key={inv.id} data-testid={`invoice-row-${inv.id}`}
              className="flex items-center justify-between text-sm rounded-xl border border-border/40 px-4 py-3">
              <span>{inv.period}</span>
              <span className="text-muted-foreground">{inv.bookings} bookings</span>
              <span className="font-outfit font-medium">£{inv.amount.toFixed(2)}</span>
              <Badge variant="outline" className="text-emerald-500 border-emerald-500/30">{inv.status}</Badge>
            </div>
          ))}
          {invoices.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-invoices-msg">No invoices yet.</p>}
        </div>
      </div>
    </div>
  );
}
